// src/services/pftReviewText.js
//
// What a PFT Uploads review item asks, in plain words (decided 2026-10-12: the notes were
// too vague to know what to check). Turns the extraction job's short reasons ("readings
// disagree", "automatic check: Pre FEV1/FVC printed 58 vs calculated 65.6", ...) into
// { title, text }: which box, what was found, and what to look at on the PDF.
//
// Done at display time from the stored reason, so cards extracted before this get the
// same wording; nothing stored changes. An unknown reason is shown as it is.
// Firebase-free and tested.

import { PFT_TEST_FIELDS } from "./pftTests";
import { reviewTargets } from "./pftReviewTargets";

const PATIENT_LABELS = {
  name: "Name", dob: "Date of birth", mrn: "MRN", sex: "Sex", phone: "Phone", address: "Address",
};
const SPECIAL_LABELS = {
  identity: "Same patient?",
  patients: "Several patients in one PDF",
  report: "Whole report",
  date: "PFT date",
};

/** The label of a box (test key, patient key or special item). */
export const reviewFieldLabel = (key) =>
  SPECIAL_LABELS[key] || PATIENT_LABELS[key] || (PFT_TEST_FIELDS.find((f) => f.key === key) || {}).label || key;

const blank = (v) => v === undefined || v === null || String(v).trim() === "";
const shown = (v) => (blank(v) ? "nothing" : String(v).trim());
const phaseOf = (tag) => (tag === "Post" ? "Post (after the inhaler)" : "Pre (before the inhaler)");
// "FEV1/FVC" -> how it reads in a sentence.
const NAMES = { "FEV1/FVC": "FEV1/FVC ratio", FEV1: "FEV1", FVC: "FVC" };
const RANGES = {
  "FEV1 (L)": "0.3 to 7 L", "FVC (L)": "0.5 to 8 L", "FEV1/FVC": "20 to 100", "FEV1 %pred": "10 to 200%",
  "FVC %pred": "10 to 200%", "RV/TLC": "15 to 80", "FEV1 reversibility (mL)": "-1000 to 2000 mL",
};
const COPIED = "The test may not have been done, and the predicted value was put in the measured box.";

// One automatic-check message from the job -> a sentence. Order matters: first match wins.
const CHECKS = [
  [/^(Pre|Post) (FEV1\/FVC) printed (\S+) vs calculated (\S+)$/, (m) =>
    `${phaseOf(m[1])}: the PDF prints an FEV1/FVC ratio of ${m[3]}, but its FEV1 ÷ FVC works out to ${m[4]}. ` +
    "One of these three numbers was probably misread. Check FEV1, FVC and the ratio on the PDF."],
  [/^(Pre|Post) FEV1\/FVC computed (\S+) vs printed (\S+)$/, (m) =>
    `${phaseOf(m[1])}: the PDF prints an FEV1/FVC ratio of ${m[3]}, but its FEV1 ÷ FVC works out to ${m[2]}. ` +
    "One of these three numbers may have been misread. Check FEV1, FVC and the ratio on the PDF."],
  [/^(Pre|Post) (FEV1|FVC) printed %pred (\S+) vs computed (\S+)$/, (m) =>
    `${phaseOf(m[1])}: the PDF prints ${m[2]} % predicted as ${m[3]}, but its ${m[2]} ÷ predicted ${m[2]} works ` +
    `out to ${m[4]}. Check ${m[2]}, predicted ${m[2]} and % predicted on the PDF.`],
  [/^(Pre|Post) (FEV1|FVC) %pred computed$/, (m) =>
    `${phaseOf(m[1])}: the PDF doesn't print ${m[2]} % predicted, so RegX calculated it from ${m[2]} ÷ predicted. ` +
    "Check that the PDF really doesn't print it."],
  [/^(Pre|Post) FEV1 > FVC$/, (m) =>
    `${phaseOf(m[1])}: FEV1 came out bigger than FVC, which isn't possible. One of the two was misread. ` +
    "Check both on the PDF."],
  [/^(Pre|Post) (FEV1|FVC) unit not printed, assumed (\S+)$/, (m) =>
    `${phaseOf(m[1])}: no unit was found next to ${m[2]}, so ${m[3] === "mL" ? "millilitres" : "litres"} ` +
    `were assumed. Check the ${m[2]} value and its unit on the PDF.`],
  [/^RV\/TLC printed (\S+) vs calculated (\S+)$/, (m) =>
    `The PDF prints RV/TLC as ${m[1]}, but its RV ÷ TLC works out to ${m[2]}. One of these numbers was probably ` +
    "misread (or a % predicted was read instead of the ratio). Check RV, TLC and RV/TLC on the PDF."],
  [/^RV\/TLC computed (\S+) vs printed (\S+)$/, (m) =>
    `The PDF prints RV/TLC as ${m[2]}, but its RV ÷ TLC works out to ${m[1]}. Check RV, TLC and RV/TLC on the PDF.`],
  [/^RV and TLC units differ; RV\/TLC not computed$/, () =>
    "RV and TLC were read with different units, so RV/TLC couldn't be worked out. Check both units on the PDF."],
  [/^RV\/TLC computed without both units printed$/, () =>
    "The PDF doesn't print RV/TLC, so RegX calculated it, but RV or TLC has no unit printed. " +
    "Check that RV and TLC use the same unit."],
  [/^(TLC|RV|DLCO) measured equals predicted \(reference value copied\?\)$/, (m) =>
    `${m[1]}: the measured value is exactly the predicted value. ${COPIED} Check whether the PDF shows a ` +
    `measured ${m[1]}; if not, clear the measured box.`],
  [/^(TLC|RV|DLCO) measured value has no predicted or %pred \(reference value copied\?\)$/, (m) =>
    `${m[1]}: there's a measured value but no predicted or % predicted beside it. It may be the predicted value ` +
    `in the wrong box. Check whether the PDF shows a measured ${m[1]}.`],
  [/^a reference value may have been copied as measured$/, () =>
    `TLC, RV or DLCO: a predicted value may have been read as a measured one. ${COPIED} ` +
    "Check those three boxes against the PDF."],
  [/^Post values present but post_bd_performed is false$/, () =>
    "Post (after the inhaler) values were found, but the report seems to say no inhaler test was done. " +
    "Check that the Post values really belong to this test."],
  [/^printed %change matches neither formula$/, () =>
    "The FEV1 % change printed on the PDF doesn't match its Pre and Post FEV1. " +
    "Check Pre FEV1, Post FEV1 and the % change on the PDF."],
  [/^FEV1 reversibility \(mL\) out of range \((\S+)\)$/, (m) =>
    `FEV1 reversibility works out to ${m[1]} mL (Post FEV1 minus Pre FEV1), outside the usual range ` +
    `(${RANGES["FEV1 reversibility (mL)"]}). Pre or Post FEV1 was probably misread or read in the wrong unit. ` +
    "Check both on the PDF."],
  [/^(?:(Pre|Post) )?(.+?) out of range \((\S+)\)$/, (m) => {
    const what = m[2];
    const where = m[1] ? `${phaseOf(m[1])}: ` : "";
    const name = what.replace(/ \(L\)$/, "").replace(/ %pred$/, " % predicted").replace(/^FEV1\/FVC$/, NAMES["FEV1/FVC"]);
    return `${where}${name} was read as ${m[3]}, outside the usual range (${RANGES[what] || "for a PFT"}). ` +
      "Check it on the PDF: it may be misread, or in the wrong unit."; }],
  [/^(\S+) reported twice, first kept$/, (m) =>
    `The value "${m[1]}" was found twice on the report and the first one was used. Check that box against the PDF.`],
];

const describeCheck = (msg) => {
  for (const [re, fn] of CHECKS) {
    const m = re.exec(msg);
    if (m) return fn(m);
  }
  return msg; // a message we don't know: as written
};

const describeDisagreement = (item) => {
  const [a, b] = item.readings || [];
  const label = reviewFieldLabel(item.field);
  if (blank(a) !== blank(b)) {
    return `The two AI reads of the PDF don't agree on ${label}: one found ${shown(blank(a) ? b : a)}, the other ` +
      "found nothing. Check whether the PDF prints it, and make sure the box has the right value (or is empty).";
  }
  if (item.readings) {
    return `The two AI reads of the PDF got different values for ${label}: ${shown(a)} and ${shown(b)}. ` +
      "Check the PDF and make sure the box has the right value.";
  }
  return `The two AI reads of the PDF got different values for ${label}. Check the PDF and make sure the box is right.`;
};

/**
 * A review item -> { title, text }: the box it's about and what to check.
 * title: for an automatic check, the box it points at; otherwise the item's field.
 */
export const describeReviewItem = (item) => {
  const field = (item && item.field) || "";
  const reason = String((item && item.reason) || "").trim();
  const targets = reviewTargets(item).test;
  const title = /^check\d+$/.test(field)
    ? targets.length ? targets.map(reviewFieldLabel).join(" · ") : "Automatic check"
    : reviewFieldLabel(field);

  let text;
  if (/^automatic check:/i.test(reason)) text = describeCheck(reason.replace(/^automatic check:\s*/i, ""));
  else if (reason === "readings disagree" || reason === "readings differ") text = describeDisagreement(item);
  else if (reason === "missing") text = `${reviewFieldLabel(field)} wasn't found on the PDF. Look for it and type it in.`;
  else if (reason === "only one of the two readings found this report")
    text = "Only one of the two AI reads found this report in the PDF. Check that it is a real PFT for this patient " +
      "and that its values are right.";
  else if (/^MRN /.test(reason)) {
    const why = reason.replace(/^MRN /, "");
    text = why === "missing"
      ? "No MRN was found on the PDF. Look for it and type it in."
      : `The MRN ${why}, so it couldn't be made a 9-digit MRN. Check it against the PDF.`;
  } else if (reason === "name missing") text = "No patient name was found on the PDF. Look for it and type it in.";
  else if (reason === "date of birth missing") text = "No date of birth was found on the PDF. Look for it and type it in.";
  else if (reason === "date of birth unreadable")
    text = "The date of birth couldn't be read as a date. Check it on the PDF and type it as a date.";
  else if (reason === "PFT date missing or unreadable")
    text = "The test date is missing or couldn't be read as a date. Check it on the PDF.";
  else text = reason || "Check this against the PDF.";
  return { title, text };
};
